import { AxiosResponse } from 'axios';
import apiClient from '../api/apiClient';
import {
  ApiResponse,
  HomeData,
  Banner,
  LectureCurationSection,
  LectureCurationSectionRequest,
  LectureCurationItemRequest,
  CoffeeChatCurationSection,
  CoffeeChatCurationSectionRequest,
} from '../../types/admin';

// 강의 목록 조회를 위한 타입 정의
export interface LectureInfo {
  id: number;
  title: string;
  thumbnail_url: string;
  tags: string[];
}

export interface LecturerInfo {
  id: number;
  name: string;
  department: string;
  image_url: string;
  tags: string[];
}

export interface LectureListItem {
  lecture_info: LectureInfo;
  lecturer_info: LecturerInfo;
}

export interface LectureListResponse {
  totalElements: number;
  totalPages: number;
  size: number;
  content: LectureListItem[];
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  first: boolean;
  last: boolean;
  numberOfElements: number;
  pageable: {
    offset: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    paged: boolean;
    pageNumber: number;
    pageSize: number;
    unpaged: boolean;
  };
  empty: boolean;
}

// 강의 큐레이션 목록 조회를 위한 타입
export interface LectureCurationListItem {
  id?: number; // API 응답에 따라 id 또는 home_lecture_section_id 사용
  home_lecture_section_id?: number; // 호환성을 위해 유지
  title: string;
  text: string;
  related_lectures: {
    relation_id: number;
    lecture_id: number;
    title: string;
    thumbnail_url: string;
    tags: string;
  }[];
}

// ===== 홈 전체 조회 =====
export const getHomeData = async (): Promise<HomeData> => {
  try {

    const response: AxiosResponse<ApiResponse<HomeData>> = await apiClient.get('/api/admin/ui/home');


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '홈 데이터 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 강의 목록 조회 API =====
export const getLectureList = async (page = 0, size = 4, sectionId?: number): Promise<LectureListResponse> => {
  try {

    const params: any = { page, size };
    if (sectionId !== undefined) {
      params.sectionId = sectionId;
    }

    const response: AxiosResponse<ApiResponse<LectureListResponse>> = await apiClient.get('/api/lectures/info/list', {
      params
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '강의 목록 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 강의 큐레이션 목록 조회 API =====
export const getLectureCurationList = async (): Promise<import('../homeCurationsApi').HomeLectureSection[]> => {
  try {

    const response: AxiosResponse<ApiResponse<import('../homeCurationsApi').HomeLectureSection[]>> = await apiClient.get('/api/ui/home/curations/lectures/list');


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '강의 큐레이션 목록 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 배너 관련 API =====

// 배너 목록 조회
export const getBannerList = async (): Promise<Banner[]> => {
  try {

    const response: AxiosResponse<ApiResponse<Banner[]>> = await apiClient.get('/api/ui/home/banners/list');


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '배너 목록 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 배너 이미지 업로드 (이미지만 업로드하고 URL 반환)
export const uploadBannerImage = async (imageFile: File): Promise<string> => {
  try {

    const formData = new FormData();
    formData.append('image', imageFile);

    const response: AxiosResponse<ApiResponse<{ imageUrl?: string; bannerImageUrl?: string }>> = await apiClient.post('/api/ui/home/banners/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });


    const data = response.data.data as any;
    return data.imageUrl || data.bannerImageUrl;
  } catch (error: any) {

    let errorMessage = '배너 이미지 업로드에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 이미지 파일입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 배너 등록 (텍스트 정보와 이미지 URL 함께 전송)
export interface BannerCreateRequest {
  title: string;
  description?: string;
  fontColorHexCode: string;
  bannerImageUrl: string;
}

export const createBanner = async (bannerData: BannerCreateRequest): Promise<Banner> => {
  try {

    const response: AxiosResponse<ApiResponse<Banner>> = await apiClient.post('/api/ui/home/banners', bannerData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '배너 등록에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 배너 수정 (텍스트 정보와 이미지 URL 함께 전송)
export interface BannerUpdateRequest {
  title: string;
  description?: string;
  fontColorHexCode: string;
  bannerImageUrl: string;
}

export const updateBanner = async (id: number, bannerData: BannerUpdateRequest): Promise<Banner> => {
  try {

    const response: AxiosResponse<ApiResponse<Banner>> = await apiClient.put(`/api/ui/home/banners/${id}`, bannerData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '배너 수정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '배너를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 배너 삭제
export const deleteBanner = async (id: number): Promise<void> => {
  try {

    const response = await apiClient.delete(`/api/ui/home/banners/${id}`);

  } catch (error: any) {

    let errorMessage = '배너 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '배너를 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 강의 큐레이션 섹션 관련 API =====

// 강의 큐레이션 섹션 등록
export const createLectureCurationSection = async (sectionData: LectureCurationSectionRequest): Promise<LectureCurationSection> => {
  try {

    const response: AxiosResponse<ApiResponse<LectureCurationSection>> = await apiClient.post('/api/ui/home/curations/lectures', sectionData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '강의 큐레이션 섹션 등록에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의 큐레이션 섹션 수정
export const updateLectureCurationSection = async (sectionId: number, sectionData: LectureCurationSectionRequest): Promise<LectureCurationSection> => {
  try {

    const response: AxiosResponse<ApiResponse<LectureCurationSection>> = await apiClient.put(`/api/ui/home/curations/lectures/${sectionId}`, sectionData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '강의 큐레이션 섹션 수정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의 큐레이션 섹션을 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의 큐레이션 섹션 삭제
export const deleteLectureCurationSection = async (sectionId: number): Promise<void> => {
  try {

    const response = await apiClient.delete(`/api/ui/home/curations/lectures/${sectionId}`);

  } catch (error: any) {

    let errorMessage = '강의 큐레이션 섹션 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의 큐레이션 섹션을 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의 큐레이션에 강의 등록
export const addLectureToCuration = async (lectureData: LectureCurationItemRequest): Promise<{ relation_id: number; lecture_id: number; section_id: number }> => {
  try {

    const response: AxiosResponse<ApiResponse<{ relation_id: number; lecture_id: number; section_id: number }>> = await apiClient.post('/api/ui/home/curations/lectures/related', lectureData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '강의 큐레이션에 강의 등록에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 409) {
      errorMessage = error.response.data?.message || '이미 존재하는 관계입니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 강의 큐레이션에서 강의 삭제
export const removeLectureFromCuration = async (sectionId: number, relatedLectureId: number): Promise<void> => {
  try {

    const response = await apiClient.delete(`/api/ui/home/curations/lectures/related/${sectionId}/${relatedLectureId}`);

  } catch (error: any) {

    let errorMessage = '강의 큐레이션에서 강의 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '강의 또는 섹션을 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// ===== 커피챗 큐레이션 섹션 관련 API =====

// 멘토 목록 조회를 위한 타입 정의
export interface MentorInfo {
  id: number;
  name: string;
  email: string;
  department: string;
  tags: string[];
  image_url: string;
  banner_url: string;
  introduction: string;
  is_temp: boolean;
  created_at: string;
}

export interface MentorListResponse {
  totalElements: number;
  totalPages: number;
  size: number;
  content: MentorInfo[];
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  first: boolean;
  last: boolean;
  numberOfElements: number;
  pageable: {
    offset: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    paged: boolean;
    pageNumber: number;
    pageSize: number;
    unpaged: boolean;
  };
  empty: boolean;
}

// 새로운 커피챗 큐레이션 응답 구조를 위한 타입
export interface CoffeeChatBanner {
  banner_id: number;
  title: string;
  description: string;
  font_color_hex_code: string;
  banner_image_url: string;
}

export interface CoffeeChatRelatedPerson {
  section_related_person_id: number;
  person_id: number;
  name: string;
  department: string;
  image_url: string;
  tags: string[];
}

export interface CoffeeChatSection {
  coffee_chat_section_id: number;
  title: string;
  text: string;
  related_persons: CoffeeChatRelatedPerson[];
}

export interface CoffeeChatCurationResponse {
  banners: CoffeeChatBanner[];
  sections: CoffeeChatSection[];
}

// 기존 호환성을 위한 타입 (deprecated)
export interface CoffeeChatCurationListItem {
  coffee_chat_section_id: number;
  title: string;
  text: string;
  related_persons: {
    section_related_person_id: number;
    person_id: number;
    name: string;
    department: string;
    image_url: string;
    tags: string[];
  }[];
}

// 멘토 목록 조회 API
export const getMentorList = async (page = 0, size = 10, status?: boolean): Promise<MentorListResponse> => {
  try {

    const params: any = { page, size };
    if (status !== undefined) {
      params.status = status;
    }

    const response: AxiosResponse<ApiResponse<MentorListResponse>> = await apiClient.get('/api/admin/mentors/list', {
      params
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '멘토 목록 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 커피챗 큐레이션 목록 조회 API (새로운 응답 구조)
export const getCoffeeChatCurationList = async (): Promise<CoffeeChatCurationResponse> => {
  try {

    const response: AxiosResponse<ApiResponse<CoffeeChatCurationResponse>> = await apiClient.get('/api/admin/ui/coffee-chat');


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '커피챗 큐레이션 목록 조회에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 커피챗 큐레이션 섹션 등록
export const createCoffeeChatCurationSection = async (sectionData: CoffeeChatCurationSectionRequest): Promise<CoffeeChatCurationSection> => {
  try {

    const response: AxiosResponse<ApiResponse<CoffeeChatCurationSection>> = await apiClient.post('/api/ui/home/curations/coffee-chats', sectionData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '커피챗 큐레이션 섹션 등록에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 커피챗 큐레이션 섹션 수정
export const updateCoffeeChatCurationSection = async (sectionId: number, sectionData: CoffeeChatCurationSectionRequest): Promise<CoffeeChatCurationSection> => {
  try {

    const response: AxiosResponse<ApiResponse<CoffeeChatCurationSection>> = await apiClient.put(`/api/ui/home/curations/coffee-chats/${sectionId}`, sectionData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '커피챗 큐레이션 섹션 수정에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '커피챗 큐레이션 섹션을 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 커피챗 큐레이션 섹션 삭제
export const deleteCoffeeChatCurationSection = async (sectionId: number): Promise<void> => {
  try {

    const response = await apiClient.delete(`/api/ui/home/curations/coffee-chats/${sectionId}`);

  } catch (error: any) {

    let errorMessage = '커피챗 큐레이션 섹션 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '커피챗 큐레이션 섹션을 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 커피챗 큐레이션에 멘토 등록
export const addMentorToCuration = async (curationId: number, personId: number): Promise<{ relation_id: number; person_id: number; section_id: number }> => {
  try {
    const requestData = {
      sectionId: curationId,
      personId: personId
    };


    const response: AxiosResponse<ApiResponse<{ relation_id: number; person_id: number; section_id: number }>> = await apiClient.post('/api/ui/home/curations/coffee-chats/related', requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });


    return response.data.data;
  } catch (error: any) {

    let errorMessage = '커피챗 큐레이션에 멘토 등록에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 400) {
      errorMessage = error.response.data?.message || '잘못된 요청입니다.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 409) {
      errorMessage = error.response.data?.message || '이미 존재하는 관계입니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};

// 커피챗 큐레이션에서 멘토 삭제
export const removeMentorFromCuration = async (sectionId: number, relatedPersonId: number): Promise<void> => {
  try {

    const response = await apiClient.delete(`/api/ui/home/curations/coffee-chats/related/${sectionId}/${relatedPersonId}`);

  } catch (error: any) {

    let errorMessage = '커피챗 큐레이션에서 멘토 삭제에 실패했습니다.';
    
    if (!error.response) {
      errorMessage = '서버에 연결할 수 없습니다. 네트워크를 확인해주세요.';
    } else if (error.response.status === 401) {
      errorMessage = '인증이 필요합니다. 다시 로그인해주세요.';
    } else if (error.response.status === 403) {
      errorMessage = '관리자 권한이 필요합니다.';
    } else if (error.response.status === 404) {
      errorMessage = '멘토 또는 섹션을 찾을 수 없습니다.';
    } else if (error.response.status >= 500) {
      errorMessage = '서버에 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    throw new Error(errorMessage);
  }
};
